import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageMeta from "../../components/common/PageMeta";
import { C, SERIF, card, pill } from "./MarketplaceShell";
import { SiteFooter, SiteHeader } from "./SiteChrome";
import { SITE_ROOT, SITE_WRAP, useSiteStyles } from "./siteStyles";
import { referenceDataApi, type StateData } from "../../services/referenceDataApi";
import { CONTACT_LIMITS, ContactFormError, submitContactForm } from "../../services/publicContactApi";
import { BookingError, sendMyBusinessApi } from "../../services/sendMyBusinessApi";

/**
 * Send my Business, at /send-my-business — a business pays to be featured in the printed Planning &
 * Development Assistance Packs posted to DA addresses in the council areas it picks.
 *
 * <p>Built from Matt's mockups, re-coloured onto the public site's own palette (deep green and mint)
 * rather than the mockups' navy and teal.</p>
 *
 * <p><b>Booking takes the professional fee, and only that.</b> With checkout on (server flag
 * Features:SendMyBusinessCheckout), the order goes to `POST /api/public/send-my-business/bookings`
 * and the customer pays $440 on Stripe Checkout, which saves their card. The monthly subscription
 * is started later by an admin, when the packs are printed. With checkout off, the order is sent
 * to Contact Requests as before and nothing is charged.</p>
 *
 * <p>The server prices a booking from the term alone; `TERMS` here is the same table, for display.
 * Change both together (SendMyBusinessPricing.cs).</p>
 */

export const TERMS = [
  { months: 12, monthly: 199 },
  { months: 24, monthly: 179 },
  { months: 36, monthly: 139, popular: true },
] as const;

export const PROFESSIONAL_FEE = 400;

/** Bump with any change to CAMPAIGN_TERMS, together with SendMyBusinessPricing.TermsVersion. */
export const TERMS_VERSION = "2026-11-03";

/** The category the order is filed under in the contact-requests inbox. */
export const SEND_MY_BUSINESS_CATEGORY = "Send my Business";

/** Whole dollars ex GST → cents including 10% GST. */
export const incGstCents = (exGstDollars: number) => exGstDollars * 110;

export const money = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-AU", { minimumFractionDigits: cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;

/**
 * The billing authorisation, with the customer's own monthly amount and term written in. The server
 * builds the same sentence (SendMyBusinessPricing.AuthorisationText) and records its copy.
 */
export function authorisationText(monthlyIncGstCents: number, termMonths: number): string {
  return (
    `Pay ${money(incGstCents(PROFESSIONAL_FEE))} today, including GST, for support and the design and creation of your business message. ` +
    `You authorise DAconnect to charge your selected monthly subscription of ${money(monthlyIncGstCents)} per month, including GST, ` +
    "to your saved payment method when your campaign commences, and monthly thereafter for your selected term of " +
    `${termMonths} months. We will confirm your commencement date before collecting the first subscription payment.`
  );
}

const TERMS_PREAMBLE =
  "For these terms, “Client” means the business purchasing the campaign, and “Subscription Term” means the fixed term selected by the Client at the time of booking.";

export const CAMPAIGN_TERMS: { title: string; body: string }[] = [
  {
    title: "Design Approval and Commencement",
    body: "The subscription cannot commence until the Client has approved the final advertisement or business message design. The Subscription Term commences with the next scheduled print run following approval, generally within one to three months of approval.",
  },
  {
    title: "Pricing and GST",
    body: "Subscription fees are charged per council, per month. All prices are exclusive of GST, which will be added at the applicable rate.",
  },
  {
    title: "Professional Fee and Artwork Changes",
    body: `A one-off professional fee of $${PROFESSIONAL_FEE} plus GST applies and covers support and the design and creation of the Client’s advertisement or business message, if required. The Client may request amendments before approving the final artwork. Following approval, amendments may be accommodated where reasonably practicable, provided production has not commenced. Once printing has commenced, amendments will take effect in the next print batch, and DAconnect may continue distributing existing stock containing the previously approved artwork until that stock is exhausted.`,
  },
  {
    title: "DAconnect System Access",
    body: "Each campaign includes full access to the DAconnect intelligence system for the duration of the Subscription Term.",
  },
  {
    title: "Subscription Term, Cancellation and Outstanding Payments",
    body: "The Client subscribes for the selected fixed Subscription Term. Subject to the Client’s rights under applicable law, cancellation before the expiry of that term requires payment of the remaining subscription fees. DAconnect may refer overdue amounts to a collection agency or commence legal proceedings for recovery. The Client is liable for reasonable recovery costs only to the extent permitted by law. Nothing in these terms excludes or restricts any right or remedy that cannot lawfully be excluded or restricted.",
  },
  {
    title: "Monthly Distribution Reports",
    body: "DAconnect will provide a monthly distribution report identifying the street, suburb and state of each development address to which a pack containing the Client’s advertisement or business message was posted. Additional development information may be accessed through the Client’s DAconnect account.",
  },
  {
    title: "Council Information Availability",
    body: "Campaign delivery is subject to the availability of the required council information. If a selected council cannot provide that information, DAconnect will propose an alternative council for the Client’s approval.",
  },
  {
    title: "Independent Service Provider",
    body: "DAconnect operates independently and is not affiliated with, endorsed by or acting on behalf of any council or government body.",
  },
];

export const ADVERT_SPECS = [
  ["Size", "90 mm wide × 80 mm high."],
  ["Preferred format", "Print-ready PDF with fonts embedded."],
  ["Other accepted formats", "High-resolution JPG or PNG."],
  ["Resolution", "300 dpi at the final print size — approximately 1,063 × 945 pixels."],
  ["Colour", "CMYK preferred for accurate print reproduction."],
] as const;

const ARTWORK_ACCEPT = ".pdf,.jpg,.jpeg,.png";
const MAX_ARTWORK_BYTES = 25 * 1024 * 1024;

const STATE_CODES = ["SA", "QLD", "NSW", "VIC", "WA", "TAS", "ACT", "NT"];

const HOW_IT_WORKS = [
  { icon: "pin", title: "Choose your council areas", body: "Reach development projects in the council areas you select, anywhere in Australia." },
  { icon: "print", title: "We print and post your packs", body: "We print your business and post directly to development applicants." },
  { icon: "chart", title: "Track your distribution", body: "Receive a monthly report of where your message went, and see further details in DAconnect." },
] as const;

const SAMPLE_REPORT = [
  { street: "Example Street", suburb: "Sample suburb", state: "QLD" },
  { street: "Sample Avenue", suburb: "Sample suburb", state: "SA" },
  { street: "Example Road", suburb: "Sample suburb", state: "NSW" },
];

const TRUST = [
  { title: "Posted to real DAs", body: "Packs go to development application addresses in your council areas." },
  { title: "Monthly report", body: "Street, suburb and state of every pack carrying your message." },
  { title: "Intelligence included", body: "Every campaign includes full access to DAconnect intelligence." },
];

const PAGE_CSS = `
.smb-choice{transition:border-color .15s,background .15s}
.smb-choice:hover{border-color:${C.link}}
.smb-hero{display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,1fr);gap:40px;align-items:center}
@media (max-width:900px){.smb-hero{grid-template-columns:1fr}}
`;

const CAPTION: CSSProperties = { display: "block", fontSize: 12.5, fontWeight: 700, color: C.ink, marginBottom: 6 };
const FIELD: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #d9d6cc",
  borderRadius: 10,
  padding: "12px 15px",
  fontSize: 14,
  fontFamily: "inherit",
  color: C.ink,
  outline: "none",
  background: "#fff",
};
const REQUIRED = <span style={{ color: "#b3261e" }}>*</span>;
const eyebrow: CSSProperties = { fontSize: 12, fontWeight: 700, letterSpacing: ".1em", color: C.link };
const h2: CSSProperties = { fontFamily: SERIF, fontSize: "clamp(24px,2.8vw,32px)", fontWeight: 500, lineHeight: 1.2, margin: "6px 0 0" };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface CouncilPick {
  state: string;
  council: string;
}

type MessageChoice = "upload" | "design";

/** The order as one readable message — it is read by a person in the contact-requests inbox. */
export interface PostalAddress {
  street: string;
  suburb: string;
  state: string;
  postcode: string;
}

export function campaignMessage(o: {
  councils: CouncilPick[];
  messageChoice: MessageChoice;
  designDetails?: string;
  months: number;
  reportEmail: string;
  phone: string;
  samplesAddress?: PostalAddress;
}): string {
  const term = TERMS.find((t) => t.months === o.months)!;
  const n = o.councils.length;
  const a = o.samplesAddress;
  return [
    "SEND MY BUSINESS — campaign request",
    "",
    `Council areas (${n}):`,
    ...o.councils.map((c) => `  • ${c.council} (${c.state})`),
    "",
    `Company message: ${o.messageChoice === "upload" ? "Uploading finished artwork — please reply so they can send it" : "Please design the message for them"}`,
    ...(o.designDetails?.trim() ? [`Design details: ${o.designDetails.trim()}`] : []),
    `Campaign term: ${term.months} months at $${term.monthly} per council, per month (+ GST)`,
    `Monthly total: ${n} × $${term.monthly} = $${(n * term.monthly).toLocaleString("en-AU")} per month (+ GST)`,
    `One-off professional fee: $${PROFESSIONAL_FEE} (+ GST)`,
    `Monthly report email: ${o.reportEmail}`,
    ...(a ? [`Sample packs to: ${a.street}, ${a.suburb} ${a.state} ${a.postcode}`] : []),
    ...(o.phone ? [`Phone: ${o.phone}`] : []),
    "",
    "Agreed to the campaign terms and conditions.",
  ].join("\n");
}

function Icon({ name }: { name: "pin" | "print" | "chart" | "upload" | "pen" | "doc" | "coins" }) {
  const paths: Record<typeof name, ReactNode> = {
    pin: <><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21Z" /><circle cx="12" cy="9.5" r="2.5" /></>,
    print: <><path d="M7 9V3h10v6" /><rect x="3" y="9" width="18" height="8" rx="2" /><path d="M7 14h10v7H7z" /></>,
    chart: <path d="M5 20V12M10 20V7M15 20v-5M20 20V4" />,
    upload: <><path d="M12 15V3M7 8l5-5 5 5" /><path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4" /></>,
    pen: <path d="m4 20 4-1L19 8l-3-3L5 16l-1 4ZM14 7l3 3" />,
    doc: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Z" /><path d="M14 3v6h6M8 13h8M8 17h6" /></>,
    coins: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" /></>,
  };
  return (
    <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.deep} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {paths[name]}
    </svg>
  );
}

function IconCircle({ name, size = 56 }: { name: Parameters<typeof Icon>[0]["name"]; size?: number }) {
  return (
    <span style={{ width: size, height: size, borderRadius: "50%", background: C.tint, display: "grid", placeItems: "center", flex: "none" }}>
      <Icon name={name} />
    </span>
  );
}

/** The pack itself — the real cover and open folder, with the business tiles a customer joins. */
function PackIllustration() {
  return (
    <img
      src="/images/partner-network/send-my-business-pack.webp"
      srcSet="/images/partner-network/send-my-business-pack-800.webp 800w, /images/partner-network/send-my-business-pack.webp 1590w"
      sizes="(max-width: 900px) 100vw, 640px"
      width={1590}
      height={989}
      alt="The DAconnect Planning & Development Assistance Pack: its cover, and the open folder holding building plans, estimates and forms beside a page of local businesses."
      style={{ display: "block", width: "100%", height: "auto", maxWidth: 680, margin: "0 auto", mixBlendMode: "multiply" }}
    />
  );
}

function StepHeading({ n, children }: { n: number; children: ReactNode }) {
  return (
    <h3 style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 19, fontWeight: 700, color: C.ink, margin: "0 0 16px" }}>
      <span style={{ width: 32, height: 32, borderRadius: "50%", background: C.deep, color: "#fff", display: "grid", placeItems: "center", fontSize: 15, flex: "none" }}>
        {n}
      </span>
      {children}
    </h3>
  );
}

export default function SendMyBusiness() {
  useSiteStyles(PAGE_CSS);

  const [states, setStates] = useState<StateData[] | null>(null);
  const [statesFailed, setStatesFailed] = useState(false);
  const [state, setState] = useState("");
  const [council, setCouncil] = useState("");
  const [councils, setCouncils] = useState<CouncilPick[]>([]);
  const [messageChoice, setMessageChoice] = useState<MessageChoice | "">("");
  const [months, setMonths] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [business, setBusiness] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [designDetails, setDesignDetails] = useState("");
  const [artwork, setArtwork] = useState<File | null>(null);
  const [artworkError, setArtworkError] = useState<string | null>(null);
  const [showSpecs, setShowSpecs] = useState(false);
  const [samples, setSamples] = useState<PostalAddress>({ street: "", suburb: "", state: "", postcode: "" });
  const [agreed, setAgreed] = useState(false);
  const [authorised, setAuthorised] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);

  // Back from Stripe: ?booking=success&session_id=… or ?booking=cancelled.
  const [params] = useSearchParams();
  const returned = params.get("booking");
  const sessionId = params.get("session_id");
  const [paidStatus, setPaidStatus] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    referenceDataApi
      .getStates()
      .then((s) => { if (!cancelled) setStates(s.filter((x) => x.councils.length > 0)); })
      .catch(() => { if (!cancelled) setStatesFailed(true); });
    sendMyBusinessApi.config().then((c) => { if (!cancelled) setCheckoutEnabled(!!c?.checkoutEnabled); });
    return () => { cancelled = true; };
  }, []);

  // The webhook that records the payment can land a few seconds after Stripe sends them back, so
  // ask a few times before settling on "we're confirming it".
  useEffect(() => {
    if (returned !== "success" || !sessionId) return;
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      const status = await sendMyBusinessApi.status(sessionId);
      if (cancelled) return;
      setPaidStatus(status);
      if (status === "awaiting_payment" && ++tries < 6) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [returned, sessionId]);

  const stateCouncils = states?.find((s) => s.abbreviation === state)?.councils ?? [];
  const isPicked = (c: CouncilPick) => councils.some((p) => p.state === c.state && p.council === c.council);

  const addCouncil = () => {
    if (!state || !council) return;
    const pick = { state, council };
    if (!isPicked(pick)) setCouncils((list) => [...list, pick]);
    setCouncil("");
  };

  const emailOk = EMAIL.test(email.trim());
  const emailsMatch = email.trim().toLowerCase() === confirmEmail.trim().toLowerCase();
  const term = TERMS.find((t) => t.months === months);
  const monthlyIncGst = term ? incGstCents(councils.length * term.monthly) : 0;
  const samplesOk = !!samples.street.trim() && !!samples.suburb.trim() && !!samples.state && /^\d{4}$/.test(samples.postcode.trim());
  const ready =
    councils.length > 0 && messageChoice !== "" && term && name.trim() && emailOk && emailsMatch && samplesOk && agreed
    && (!checkoutEnabled || authorised);

  const setSample = (key: keyof PostalAddress, value: string) => setSamples((s) => ({ ...s, [key]: value }));

  const pickArtwork = (file: File | null) => {
    setArtworkError(null);
    if (file && file.size > MAX_ARTWORK_BYTES) {
      setArtwork(null);
      setArtworkError("That file is larger than 25 MB. Please choose a smaller one, or send it to us after booking.");
      return;
    }
    setArtwork(file);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready || !term || sending) return;
    setSending(true);
    setError(null);
    const address = { street: samples.street.trim(), suburb: samples.suburb.trim(), state: samples.state, postcode: samples.postcode.trim() };
    try {
      if (checkoutEnabled) {
        const url = await sendMyBusinessApi.book(
          {
            councilAreas: councils.map((c) => ({ state: c.state, name: c.council })),
            termMonths: term.months,
            messageChoice,
            designDetails: designDetails.trim() || undefined,
            name: name.trim(),
            business: business.trim() || undefined,
            phone: phone.trim() || undefined,
            email: email.trim(),
            samplesAddress: address,
            acceptedTerms: agreed,
            authorisedBilling: authorised,
            termsVersion: TERMS_VERSION,
          },
          messageChoice === "upload" ? artwork : null,
        );
        window.location.assign(url);
        return;
      }
      await submitContactForm({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        business: business.trim() || undefined,
        enquiryCategory: SEND_MY_BUSINESS_CATEGORY,
        message: campaignMessage({
          councils, messageChoice, designDetails, months: term.months, reportEmail: email.trim(), phone: phone.trim(), samplesAddress: address,
        }),
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof ContactFormError || err instanceof BookingError ? err.message : "We couldn't send that just then. Please try again.");
    }
    setSending(false);
  };

  return (
    <div className="site-root" style={SITE_ROOT}>
      <PageMeta
        title="Send my Business | DAconnect"
        description="Feature your business in DAconnect Planning & Development Assistance Packs, posted to development application addresses in your chosen council areas."
      />
      <SiteHeader active="send" />

      {/* HERO */}
      <section style={{ ...SITE_WRAP, padding: "18px 28px 0" }}>
        <nav aria-label="Breadcrumb" style={{ fontSize: 13, color: C.bodyMuted }}>
          <Link to="/find" style={{ color: C.bodyMuted }}>Marketplace</Link>
          <span style={{ margin: "0 8px" }}>/</span>
          <span aria-current="page" style={{ color: C.ink }}>Send my Business</span>
        </nav>
        <div className="smb-hero" style={{ marginTop: 24 }}>
          <div>
            <div style={eyebrow}>PLANNING &amp; DEVELOPMENT ASSISTANCE PACKS</div>
            <h1 style={{ fontFamily: SERIF, fontSize: "clamp(34px,4.6vw,56px)", fontWeight: 500, lineHeight: 1.08, margin: "12px 0 0" }}>
              Send your business <b style={{ color: C.deep, fontWeight: 600 }}>to their next project.</b>
            </h1>
            <p style={{ fontSize: 17, color: C.inkMuted, lineHeight: 1.6, margin: "18px 0 0", maxWidth: 560 }}>
              Feature your business in DAconnect Planning &amp; Development Assistance Packs, posted to development
              application addresses in your chosen council areas.
            </p>
            <p style={{ fontSize: 14.5, color: C.bodyMuted, margin: "14px 0 0" }}>
              Campaigns from ${TERMS[2].monthly} per council, per month + GST
            </p>
            <a
              href="#get-started"
              className="site-pill"
              style={{ ...pill, fontSize: 15, background: C.deep, color: "#fff", padding: "14px 28px", marginTop: 22, boxShadow: "0 10px 24px rgba(20,83,45,.22)" }}
            >
              Choose your council →
            </a>
          </div>
          <PackIllustration />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section aria-labelledby="smb-how" style={{ ...SITE_WRAP, padding: "40px 28px 0" }}>
        <div style={eyebrow}>HOW IT WORKS</div>
        <h2 id="smb-how" style={h2}>
          A useful pack. <b style={{ color: C.deep, fontWeight: 600 }}>A lasting place for your business.</b>
        </h2>
        <p style={{ fontSize: 16, color: C.inkMuted, lineHeight: 1.6, margin: "10px 0 0", maxWidth: 760 }}>
          A practical folder that brings together the DA information, plans, estimates, quotes, fees and forms for a
          project — with your company's message close at hand.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16, marginTop: 22 }}>
          {HOW_IT_WORKS.map((s) => (
            <div key={s.title} data-testid="smb-step" style={{ ...card, borderRadius: 16, padding: "22px 22px", display: "flex", gap: 16, alignItems: "center" }}>
              <IconCircle name={s.icon} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>{s.title}</div>
                <p style={{ fontSize: 14, color: C.inkMuted, lineHeight: 1.5, margin: "4px 0 0" }}>{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EVIDENCE */}
      <section style={{ background: "linear-gradient(180deg,#e2f3e7,#f2faf4)", borderTop: "1px solid #d3ecdb", borderBottom: "1px solid #d3ecdb", marginTop: 48 }}>
        <div className="smb-hero" style={{ ...SITE_WRAP, padding: "44px 28px" }}>
          <div>
            <div style={eyebrow}>EVIDENCE</div>
            <h2 style={h2}>
              See where your message <b style={{ color: C.deep, fontWeight: 600 }}>has been sent.</b>
            </h2>
            <p style={{ fontSize: 16, color: C.inkMuted, lineHeight: 1.6, margin: "12px 0 0", maxWidth: 520 }}>
              Every month we email you a report of each development that received your company's message — street,
              suburb and state. Log in to your DAconnect account for further project details.
            </p>
          </div>
          <div style={{ ...card, padding: "20px 22px", boxShadow: "0 18px 40px -20px rgba(20,83,45,.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.ink }}>Monthly distribution report</div>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", background: C.tint, color: C.deep, padding: "5px 10px", borderRadius: 99 }}>
                SAMPLE REPORT
              </span>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
              <thead>
                <tr style={{ background: "#f4f3ee", textAlign: "left" }}>
                  {["Street", "Suburb", "State"].map((h) => (
                    <th key={h} style={{ padding: "9px 12px", fontWeight: 700, color: C.ink, border: `1px solid ${C.line}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SAMPLE_REPORT.map((r) => (
                  <tr key={r.street}>
                    <td style={{ padding: "9px 12px", border: `1px solid ${C.line}` }}>{r.street}</td>
                    <td style={{ padding: "9px 12px", border: `1px solid ${C.line}` }}>{r.suburb}</td>
                    <td style={{ padding: "9px 12px", border: `1px solid ${C.line}` }}>{r.state}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 12.5, color: C.bodyMuted, margin: "12px 0 0" }}>
              Please log in to your DAconnect account for further details.
            </p>
          </div>
        </div>
      </section>

      {/* GET STARTED */}
      <section id="get-started" aria-labelledby="smb-start" style={{ ...SITE_WRAP, padding: "48px 28px 56px", maxWidth: 1080, scrollMarginTop: 80 }}>
        <div style={{ textAlign: "center" }}>
          <div style={eyebrow}>SEND MY BUSINESS</div>
          <h2 id="smb-start" style={h2}>Build your campaign</h2>
          <p style={{ fontSize: 16, color: C.inkMuted, margin: "8px 0 0" }}>
            Choose where you want to be seen. We'll help you prepare your message.
          </p>
        </div>

        {returned === "success" ? (
          <div data-testid="smb-paid" role="status" style={{ ...card, padding: "32px 34px", marginTop: 28, textAlign: "center" }}>
            <h3 style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 500, color: C.ink, margin: "0 0 8px" }}>
              {paidStatus && paidStatus !== "awaiting_payment" ? "Thank you — your booking is confirmed." : "Thank you — we're confirming your payment."}
            </h3>
            <p style={{ fontSize: 15, lineHeight: 1.7, color: C.bodyMuted, margin: "0 auto 20px", maxWidth: 580 }}>
              {paidStatus && paidStatus !== "awaiting_payment"
                ? "Your professional fee has been paid and your card is saved for your monthly subscription. "
                : "This usually takes a few seconds, and we'll email you as soon as it's done. "}
              Nothing more is charged until your campaign commences: your subscription starts with the next print run after you approve
              your design, and we'll email you the commencement date before the first monthly payment.
            </p>
            <Link to="/find" className="site-pill" style={{ ...pill, background: C.deep, color: "#fff" }}>
              Back to the marketplace
            </Link>
          </div>
        ) : sent ? (
          <div data-testid="smb-sent" role="status" style={{ ...card, padding: "32px 34px", marginTop: 28, textAlign: "center" }}>
            <h3 style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 500, color: C.ink, margin: "0 0 8px" }}>
              Thanks — we've got your campaign request.
            </h3>
            <p style={{ fontSize: 15, lineHeight: 1.7, color: C.bodyMuted, margin: "0 auto 20px", maxWidth: 560 }}>
              Nothing has been charged yet. Someone from the team will be in touch at{" "}
              <strong style={{ color: C.ink }}>{email.trim()}</strong> to confirm your council areas
              {messageChoice === "upload" ? ", collect your artwork" : ", start on your design"} and set up payment.
            </p>
            <Link to="/find" className="site-pill" style={{ ...pill, background: C.deep, color: "#fff" }}>
              Back to the marketplace
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} noValidate style={{ display: "flex", flexDirection: "column", gap: 18, marginTop: 28 }}>
            {returned === "cancelled" && (
              <div role="status" data-testid="smb-cancelled" style={{ background: "#fff8e6", border: "1px solid #f1dfae", borderRadius: 12, padding: "12px 15px", fontSize: 14, color: "#6b4e00" }}>
                Payment cancelled — nothing has been charged. Your choices below need re-entering; please try again when you're ready.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 18 }}>
              {/* 1. COUNCILS */}
              <div style={{ ...card, padding: "24px 26px" }}>
                <StepHeading n={1}>Please tell us what Council(s) you want?</StepHeading>
                {statesFailed ? (
                  <p role="alert" style={{ fontSize: 13.5, color: "#8f231c", margin: 0 }}>
                    The list of councils couldn't be loaded just now. Please refresh the page to try again.
                  </p>
                ) : (
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 12 }}>
                      <div>
                        <label htmlFor="smb-state" style={CAPTION}>State</label>
                        <select
                          id="smb-state"
                          value={state}
                          onChange={(e) => { setState(e.target.value); setCouncil(""); }}
                          disabled={!states}
                          style={FIELD}
                        >
                          <option value="">{states ? "Select a state" : "Loading…"}</option>
                          {states?.map((s) => (
                            <option key={s.abbreviation} value={s.abbreviation}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="smb-council" style={CAPTION}>Council</label>
                        <select id="smb-council" value={council} onChange={(e) => setCouncil(e.target.value)} disabled={!state} style={FIELD}>
                          <option value="">Select a council</option>
                          {stateCouncils.map((c) => (
                            <option key={c} value={c} disabled={isPicked({ state, council: c })}>{c}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={addCouncil}
                      disabled={!council}
                      style={{
                        marginTop: 12,
                        background: "none",
                        border: "none",
                        padding: 0,
                        fontFamily: "inherit",
                        fontSize: 14,
                        fontWeight: 700,
                        color: council ? C.link : C.footInk,
                        cursor: council ? "pointer" : "default",
                      }}
                    >
                      + Add {councils.length ? "another " : ""}Council
                    </button>
                    {councils.length > 0 && (
                      <ul aria-label="Chosen councils" style={{ listStyle: "none", padding: 0, margin: "14px 0 0", display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {councils.map((c) => (
                          <li
                            key={`${c.state}-${c.council}`}
                            style={{ display: "flex", alignItems: "center", gap: 8, background: C.tint, color: C.deep, fontSize: 13, fontWeight: 600, padding: "6px 8px 6px 12px", borderRadius: 99 }}
                          >
                            {c.council} ({c.state})
                            <button
                              type="button"
                              aria-label={`Remove ${c.council}`}
                              onClick={() => setCouncils((list) => list.filter((p) => p !== c))}
                              style={{ border: "none", background: "transparent", color: C.deep, cursor: "pointer", fontSize: 15, lineHeight: 1, padding: "0 4px" }}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>

              {/* 2. MESSAGE */}
              <fieldset style={{ ...card, padding: "24px 26px", margin: 0, minWidth: 0 }}>
                <legend style={{ padding: 0, float: "left", width: "100%" }}>
                  <StepHeading n={2}>Prepare your company's message</StepHeading>
                </legend>
                <div style={{ display: "flex", flexDirection: "column", gap: 10, clear: "both" }}>
                  {([
                    { key: "upload", icon: "upload", label: "Upload my finished artwork" },
                    { key: "design", icon: "pen", label: "Please design my message for us" },
                  ] as const).map((o) => {
                    const on = messageChoice === o.key;
                    return (
                      <label
                        key={o.key}
                        className="smb-choice"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 14,
                          border: `1.5px solid ${on ? C.deep : C.cardLine}`,
                          background: on ? C.tint : "#fff",
                          borderRadius: 12,
                          padding: "12px 14px",
                          cursor: "pointer",
                          fontSize: 15,
                          fontWeight: 600,
                          color: C.ink,
                        }}
                      >
                        <input
                          type="radio"
                          name="smb-message"
                          value={o.key}
                          checked={on}
                          onChange={() => setMessageChoice(o.key)}
                          style={{ accentColor: C.deep, width: 18, height: 18, margin: 0 }}
                        />
                        <IconCircle name={o.icon} size={40} />
                        {o.label}
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  aria-expanded={showSpecs}
                  aria-controls="smb-specs"
                  onClick={() => setShowSpecs((v) => !v)}
                  className="site-pill"
                  style={{ ...pill, marginTop: 12, fontFamily: "inherit", fontSize: 13.5, cursor: "pointer", border: `1.5px solid ${C.deep}`, background: "#fff", color: C.deep, padding: "8px 16px" }}
                >
                  {showSpecs ? "Hide" : "View"} advert specifications
                </button>
                {showSpecs && (
                  <div id="smb-specs" data-testid="smb-specs" style={{ marginTop: 12, background: "#f7f6f1", borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.ink, marginBottom: 6 }}>Advert specifications</div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: C.inkMuted, lineHeight: 1.6 }}>
                      {ADVERT_SPECS.map(([label, value]) => (
                        <li key={label}><b style={{ color: C.ink }}>{label}:</b> {value}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {messageChoice === "upload" && (
                  <div style={{ marginTop: 14 }}>
                    {checkoutEnabled ? (
                      <>
                        <label htmlFor="smb-artwork" style={CAPTION}>Your artwork (PDF, JPG or PNG, up to 25 MB)</label>
                        <input
                          id="smb-artwork"
                          type="file"
                          accept={ARTWORK_ACCEPT}
                          onChange={(e) => pickArtwork(e.target.files?.[0] ?? null)}
                          style={{ fontSize: 13.5, maxWidth: "100%" }}
                        />
                        {artworkError && <div role="alert" style={{ fontSize: 12.5, color: "#b3261e", marginTop: 6 }}>{artworkError}</div>}
                        <p style={{ fontSize: 13, color: C.bodyMuted, margin: "8px 0 0" }}>
                          Not ready yet? Leave it — you can send it to us after booking. We'll confirm it with you before printing.
                        </p>
                      </>
                    ) : (
                      <p style={{ fontSize: 13, color: C.bodyMuted, margin: 0 }}>
                        We'll reply with where to send your artwork, and confirm it with you before printing.
                      </p>
                    )}
                  </div>
                )}

                {messageChoice === "design" && (
                  <div style={{ marginTop: 14 }}>
                    <div
                      role="status"
                      data-testid="smb-design-callout"
                      style={{ background: C.mint, color: C.deep, borderRadius: 12, padding: "12px 14px", fontSize: 14.5, fontWeight: 700 }}
                    >
                      Someone will contact you shortly — please supply details below.
                    </div>
                    <label htmlFor="smb-design-details" style={{ ...CAPTION, marginTop: 12 }}>Details for your message</label>
                    <textarea
                      id="smb-design-details"
                      value={designDetails}
                      onChange={(e) => setDesignDetails(e.target.value)}
                      maxLength={2000}
                      rows={4}
                      placeholder="What you do, the services to feature, your phone, email and website, and anything you'd like it to say."
                      style={{ ...FIELD, resize: "vertical" }}
                    />
                  </div>
                )}
              </fieldset>
            </div>

            {/* 3. TERM */}
            <div>
              <div style={{ padding: "6px 4px 0" }}>
                <StepHeading n={3}>Choose your campaign term</StepHeading>
              </div>
              <div role="radiogroup" aria-label="Campaign term" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16 }}>
                {TERMS.map((t) => {
                  const on = months === t.months;
                  const popular = "popular" in t && t.popular;
                  return (
                    <div
                      key={t.months}
                      data-testid={`term-${t.months}`}
                      style={{
                        ...card,
                        borderRadius: 16,
                        padding: "24px 22px 22px",
                        textAlign: "center",
                        position: "relative",
                        border: `1.5px solid ${on || popular ? C.deep : C.cardLine}`,
                        background: on ? C.tint : "#fff",
                      }}
                    >
                      {popular && (
                        <span style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: C.mint, color: C.deep, fontSize: 11, fontWeight: 700, letterSpacing: ".06em", padding: "4px 12px", borderRadius: 99, whiteSpace: "nowrap" }}>
                          MOST POPULAR
                        </span>
                      )}
                      <div style={{ fontSize: 17, fontWeight: 700, color: C.ink }}>{t.months} months</div>
                      <div style={{ fontFamily: SERIF, fontSize: 44, fontWeight: 600, color: C.deep, lineHeight: 1.1, marginTop: 6 }}>${t.monthly}</div>
                      <div style={{ fontSize: 14, color: C.bodyMuted }}>per council, per month</div>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setMonths(t.months)}
                        className="site-pill"
                        style={{
                          ...pill,
                          width: "100%",
                          marginTop: 16,
                          fontFamily: "inherit",
                          cursor: "pointer",
                          border: `1.5px solid ${C.deep}`,
                          background: on || popular ? C.deep : "#fff",
                          color: on || popular ? "#fff" : C.deep,
                        }}
                      >
                        {on ? "✓ Selected" : `Select ${t.months} months`}
                      </button>
                    </div>
                  );
                })}
              </div>
              <p style={{ fontSize: 13, color: C.bodyMuted, textAlign: "center", margin: "12px 0 0" }}>
                Prices are per council, per month, and exclude GST. Every plan includes full access to DAconnect intelligence.
              </p>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 18, background: C.tint, border: "1px solid #d3ecdb", borderRadius: 14, padding: "16px 22px" }}>
              <IconCircle name="coins" size={46} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.ink }}>One-off professional fee · ${PROFESSIONAL_FEE} + GST</div>
                <div style={{ fontSize: 14, color: C.inkMuted, marginTop: 2 }}>
                  Includes full client support and the design and creation of your business message, if required.
                </div>
              </div>
            </div>

            {term && councils.length > 0 && (
              <div data-testid="smb-summary" style={{ fontSize: 14.5, color: C.ink, textAlign: "right" }}>
                {councils.length} {councils.length === 1 ? "council" : "councils"} × ${term.monthly} ={" "}
                <b style={{ color: C.deep }}>${(councils.length * term.monthly).toLocaleString("en-AU")} per month</b> + GST
                {" "}({money(monthlyIncGst)} including GST), for {term.months} months
              </div>
            )}

            {/* 4. REPORT + DETAILS */}
            <div style={{ ...card, padding: "24px 26px" }}>
              <StepHeading n={4}>Your details</StepHeading>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, margin: "0 0 12px" }}>
                4a. What email address should we send your monthly report to?
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: "16px 18px" }}>
                <div>
                  <label htmlFor="smb-email" style={CAPTION}>Email address {REQUIRED}</label>
                  <input id="smb-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={CONTACT_LIMITS.email} autoComplete="email" placeholder="name@yourbusiness.com.au" required style={FIELD} />
                </div>
                <div>
                  <label htmlFor="smb-email-confirm" style={CAPTION}>Confirm email address {REQUIRED}</label>
                  <input id="smb-email-confirm" type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} maxLength={CONTACT_LIMITS.email} autoComplete="email" placeholder="name@yourbusiness.com.au" required style={FIELD} />
                  {confirmEmail && !emailsMatch && (
                    <div style={{ fontSize: 12.5, color: "#b3261e", marginTop: 6 }}>The email addresses don't match.</div>
                  )}
                </div>
                <div>
                  <label htmlFor="smb-name" style={CAPTION}>Your name {REQUIRED}</label>
                  <input id="smb-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={CONTACT_LIMITS.name} autoComplete="name" required style={FIELD} />
                </div>
                <div>
                  <label htmlFor="smb-business" style={CAPTION}>Business name</label>
                  <input id="smb-business" value={business} onChange={(e) => setBusiness(e.target.value)} maxLength={CONTACT_LIMITS.business} autoComplete="organization" style={FIELD} />
                </div>
                <div>
                  <label htmlFor="smb-phone" style={CAPTION}>Phone</label>
                  <input id="smb-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={CONTACT_LIMITS.phone} autoComplete="tel" placeholder="Optional" style={FIELD} />
                </div>
              </div>

              <div style={{ fontSize: 15, fontWeight: 700, color: C.ink, margin: "24px 0 12px" }}>
                4b. What postal address should we send your Development Pack samples to?
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: "16px 18px" }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label htmlFor="smb-street" style={CAPTION}>Street address {REQUIRED}</label>
                  <input id="smb-street" value={samples.street} onChange={(e) => setSample("street", e.target.value)} maxLength={200} autoComplete="street-address" required style={FIELD} />
                </div>
                <div>
                  <label htmlFor="smb-suburb" style={CAPTION}>Suburb {REQUIRED}</label>
                  <input id="smb-suburb" value={samples.suburb} onChange={(e) => setSample("suburb", e.target.value)} maxLength={100} autoComplete="address-level2" required style={FIELD} />
                </div>
                <div>
                  <label htmlFor="smb-samples-state" style={CAPTION}>State {REQUIRED}</label>
                  <select id="smb-samples-state" value={samples.state} onChange={(e) => setSample("state", e.target.value)} required style={FIELD}>
                    <option value="">Select</option>
                    {STATE_CODES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="smb-postcode" style={CAPTION}>Postcode {REQUIRED}</label>
                  <input id="smb-postcode" inputMode="numeric" value={samples.postcode} onChange={(e) => setSample("postcode", e.target.value.replace(/\D/g, "").slice(0, 4))} autoComplete="postal-code" required style={FIELD} />
                </div>
              </div>
            </div>

            <div style={{ ...card, padding: "18px 22px", display: "flex", gap: 16, alignItems: "flex-start" }}>
              <IconCircle name="doc" size={46} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>Before you get started</div>
                <p style={{ fontSize: 14, color: C.inkMuted, lineHeight: 1.55, margin: "4px 0 0" }}>
                  Your subscription cannot commence until you have approved your advertisement or business message design. It will
                  then begin with the next scheduled print run, usually within 1–3 months of approval.
                </p>
                <button
                  type="button"
                  aria-expanded={showTerms}
                  aria-controls="smb-terms"
                  onClick={() => setShowTerms((v) => !v)}
                  style={{ background: "none", border: "none", padding: 0, marginTop: 8, fontFamily: "inherit", fontSize: 13.5, fontWeight: 700, color: C.link, cursor: "pointer" }}
                >
                  {showTerms ? "Hide" : "Read"} the campaign terms and conditions
                </button>
                {showTerms && (
                  <div id="smb-terms" data-testid="smb-terms" style={{ fontSize: 13.5, color: C.inkMuted, lineHeight: 1.6, marginTop: 10 }}>
                    <div style={{ fontWeight: 700, color: C.ink }}>DAconnect — Key Terms &amp; Conditions</div>
                    <p style={{ margin: "6px 0 0" }}>{TERMS_PREAMBLE}</p>
                    <ol style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                      {CAMPAIGN_TERMS.map((t) => (
                        <li key={t.title} style={{ marginTop: 6 }}>
                          <b style={{ color: C.ink }}>{t.title}.</b> {t.body}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}
              </div>
            </div>

            {error && (
              <div role="alert" data-testid="smb-error" style={{ background: "#fdf2f2", border: "1px solid #f3c9c6", borderRadius: 12, padding: "12px 15px", fontSize: 13.5, color: "#8f231c" }}>
                {error}
              </div>
            )}

            {checkoutEnabled && (
              <div data-testid="smb-authorisation" style={{ ...card, padding: "20px 22px", border: `1.5px solid ${C.deep}` }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>Payment today, and your monthly subscription later</div>
                {term && councils.length > 0 ? (
                  <>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 24px", margin: "10px 0 0", fontSize: 14, color: C.ink }}>
                      <span>Today: <b>{money(incGstCents(PROFESSIONAL_FEE))}</b> incl. GST</span>
                      <span>Monthly, from commencement: <b>{money(monthlyIncGst)}</b> incl. GST</span>
                      <span>Term: <b>{term.months} months</b></span>
                    </div>
                    <p data-testid="smb-authorisation-text" style={{ fontSize: 13.5, color: C.inkMuted, lineHeight: 1.6, margin: "10px 0 0" }}>
                      {authorisationText(monthlyIncGst, term.months)}
                    </p>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 14, color: C.ink, cursor: "pointer", marginTop: 12 }}>
                      <input type="checkbox" checked={authorised} onChange={(e) => setAuthorised(e.target.checked)} style={{ accentColor: C.deep, width: 18, height: 18, margin: "1px 0 0", flex: "none" }} />
                      I authorise DAconnect to charge {money(monthlyIncGst)} per month (including GST) to my saved payment method from my campaign's
                      commencement date, for {term.months} months.
                    </label>
                  </>
                ) : (
                  <p style={{ fontSize: 13.5, color: C.bodyMuted, margin: "8px 0 0" }}>
                    Choose your councils and term above to see your monthly amount.
                  </p>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14.5, color: C.ink, cursor: "pointer" }}>
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ accentColor: C.deep, width: 18, height: 18, margin: 0 }} />
                I agree to the campaign terms and conditions
              </label>
              <button
                type="submit"
                data-testid="smb-submit"
                disabled={!ready || sending}
                className="site-pill"
                style={{
                  ...pill,
                  fontSize: 15,
                  fontFamily: "inherit",
                  border: "none",
                  padding: "14px 30px",
                  background: ready ? C.deep : "#b8c2b8",
                  color: "#fff",
                  cursor: ready && !sending ? "pointer" : "not-allowed",
                }}
              >
                {checkoutEnabled
                  ? sending ? "Opening secure payment…" : `Pay ${money(incGstCents(PROFESSIONAL_FEE))} and book →`
                  : sending ? "Sending…" : "Send my campaign request →"}
              </button>
            </div>

            <p style={{ fontSize: 12.5, color: C.footInk, textAlign: "center", margin: "6px 0 0", lineHeight: 1.6 }}>
              Council availability is subject to data access. If your chosen council is unavailable, we'll suggest an alternative.
              <br />
              DAconnect is independent and is not affiliated with any council or government body.
            </p>
          </form>
        )}
      </section>

      <SiteFooter trust={TRUST} />
    </div>
  );
}
